import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { forwardRef, NgModule, Provider } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  NgxGoogleAnalyticsModule,
  NgxGoogleAnalyticsRouterModule,
} from 'ngx-google-analytics';

import { environment } from '@environments/environment';
import { ImageUploadModule } from '@image-upload/image-upload.module';
import { GlobalLoaderInterceptor } from '@interceptors/global-loader-interceptor';
import { ImpersonationInterceptor } from '@interceptors/impersonation-interceptor';
import { getHunPaginatorIntl } from '@shared/interfaces/hun-paginator-intl';

import { storeDevtoolsModule } from '../build-specifics/index';
import { AgeVerificationModule } from './age-verification/age-verification.module';
import { ApiModule } from './api/api.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BasketDiscountsModule } from './basket-discounts/basket-discounts.module';
import { EasylotAdminSharedModule } from './easylot-admin/modules/easylot-admin-shared/easylot-admin-shared.module';
import { ImpersonationEffects } from './easylot-admin/modules/impersonation/store/impersonation.effects';
import { ImpersonationFeature } from './easylot-admin/modules/impersonation/store/impersonation.slice';
import { MerchantApprovalsModule } from './easylot-admin/modules/merchant-approvals/merchant-approvals.module';
import { ErrorModule } from './error/error.module';
import { ApiAuthInterceptor } from './interceptors/api-auth-interceptor';
import { ApiErrorInterceptor } from './interceptors/api-error-interceptor';
import { LayoutModule } from './layout/layout.module';
import { MerchantAccountModule } from './merchant-account/merchant-account.module';
import { ProductImportModule } from './product-import/product-import.module';
import { SandboxModule } from './sandbox/sandbox.module';
import { SegmentDiscountsModule } from './segment-discounts/segment-discounts.module';
import { SharedModule } from './shared/shared.module';
import { TelesalesModule } from './telesales/telesales.module';
import { VipDiscountsBuyingModule } from './vip-discounts/modules/vip-discounts-buying/vip-discounts-buying.module';
import { VipDiscountsSellingModule } from './vip-discounts/modules/vip-discounts-selling/vip-discounts-selling.module';
import { VipDiscountsModule } from './vip-discounts/vip-discounts.module';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

export const API_ERROR_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ApiErrorInterceptor),
  multi: true,
};

export const API_AUTH_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ApiAuthInterceptor),
  multi: true,
};

export const GLOBAL_LOADER_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => GlobalLoaderInterceptor),
  multi: true,
};
export const IMPERSONATION_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ImpersonationInterceptor),
  multi: true,
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    LayoutModule,
    SharedModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    StoreModule.forRoot({}),
    StoreModule.forFeature(ImpersonationFeature),
    EffectsModule.forRoot([]),
    EffectsModule.forFeature([ImpersonationEffects]),
    ErrorModule.forRoot(),
    NgxGoogleAnalyticsModule.forRoot(environment.analyticsTrackingCode),
    NgxGoogleAnalyticsRouterModule,
    storeDevtoolsModule,
    AgeVerificationModule,
    ImageUploadModule,
    ProductImportModule,
    ApiModule.forRoot({ rootUrl: '/api/v1' }),
    MerchantApprovalsModule,
    EasylotAdminSharedModule,
    SandboxModule,
    VipDiscountsModule,
    TelesalesModule,
    MerchantAccountModule,
    VipDiscountsSellingModule,
    VipDiscountsBuyingModule,
    SegmentDiscountsModule,
    BasketDiscountsModule,
  ],
  providers: [
    ApiErrorInterceptor,
    ApiAuthInterceptor,
    GlobalLoaderInterceptor,
    ImpersonationInterceptor,
    API_ERROR_INTERCEPTOR_PROVIDER,
    API_AUTH_INTERCEPTOR_PROVIDER,
    GLOBAL_LOADER_INTERCEPTOR_PROVIDER,
    IMPERSONATION_INTERCEPTOR_PROVIDER,
    { provide: MatPaginatorIntl, useValue: getHunPaginatorIntl() },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
